import type { Draft, Option } from '@resi-media/resi-ui';
import type { TrackEventProp } from '@studio/constants/tracking-constants';

export enum AVAILABILITY_TYPE {
  IMMEDIATELY = 'immediately',
  NEXT_REPLAY = 'next-replay',
}

export type ChecksumTask = {
  cancel(): void;
  hash(): Promise<string | null>;
};

export type UploadVideoForm = {
  airDate: Date;
  airTime: string;
  availabilityToView: string;
  createWebVideo: boolean;
  description: string;
  detectQrCodes: boolean;
  encoderChannelId: string;
  playlistIds: string[];
  regionId: string;
  selectedVideoFile: string;
  tags: Draft.SelectCreatable.MultiValue<Option>;
  title: string;
  uploadType: string;
  webChannelId: string;
};

export type AddError = (type: VALIDATIONS) => void;
export type AddWarning = (type: VALIDATIONS) => void;

type _PersistentUrls = {
  dashUrl: string;
  dashUrlEnabled: boolean;
  enabled: boolean;
  hlsUrl: string;
  hlsUrlEnabled: boolean;
};

export type WebEventProfile = {
  branded: boolean;
  bucket: string;
  chromeCastAppId: string;
  delaySeconds: number;
  deleteAfter: number;
  description: string;
  dvrWindowSeconds?: number;
  loadBalPrefix: string;
  name: string;
  persistentUrls: _PersistentUrls;
  regionId: string;
  type: string;
  uuid: string;
};

export type VideoUploadTrackEventProperties = {
  [TrackEventProp.FILE_NAME]?: string;
  [TrackEventProp.FILE_SIZE]?: number;
  [TrackEventProp.VIDEO_UPLOAD_GOP_DURATION]?: number;
  [TrackEventProp.VIDEO_UPLOAD_KEYFRAME_MBPS]?: number;
  [TrackEventProp.VIDEO_UPLOAD_FRAMERATE]?: number;
  [TrackEventProp.VIDEO_UPLOAD_RESOLUTION_WIDTH]?: number;
  [TrackEventProp.VIDEO_UPLOAD_RESOLUTION_HEIGHT]?: number;
  [TrackEventProp.VIDEO_UPLOAD_VIDEO_BITRATE_MBPS]?: number;
  [TrackEventProp.VIDEO_UPLOAD_VIDEO_CHANNELS]?: number;
  [TrackEventProp.VIDEO_UPLOAD_AUDIO_BITRATE_KBPS]?: number;
  [TrackEventProp.VIDEO_UPLOAD_AUDIO_SAMPLE_RATE_KHZ]?: number;
  [TrackEventProp.VIDEO_UPLOAD_AUDIO_CHANNELS]?: number;
  [TrackEventProp.VIDEO_UPLOAD_SAMPLE_DURATION]?: number;
  [TrackEventProp.VIDEO_UPLOAD_TIMESCALE]?: number;
  [TrackEventProp.VIDEO_UPLOAD_ERROR_CODES]?: string[];
  [TrackEventProp.SUCCESS]?: string;
};

export type ValidationsObject = {
  [key: string]: string;
};

export enum VALIDATIONS {
  BACK_END = 'BACK_END',
  BLANK_EVENT_NAME = 'BLANK_EVENT_NAME',
  BLANK_EVENT_PROFILE = 'BLANK_EVENT_PROFILE',
  BLANK_WEB_EVENT_PROFILE = 'BLANK_WEB_EVENT_PROFILE',
  FILE_SIZE_0 = 'FILE_SIZE_0',
  INVALID_AUDIO_SAMPLERATE = 'INVALID_AUDIO_SAMPLERATE',
  INVALID_AUDIO_TYPE = 'INVALID_AUDIO_TYPE',
  INVALID_DIMENSIONS = 'INVALID_DIMENSIONS',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  INVALID_FORMAT = 'INVALID_FORMAT',
  INVALID_FPS = 'INVALID_FPS',
  INVALID_FRAMERATE_MODE = 'INVALID_FRAMERATE_MODE',
  INVALID_PIXEL_FORMAT = 'INVALID_PIXEL_FORMAT',
  KEYFRAME_INCONSISTENCY = 'KEYFRAME_INCONSISTENCY',
  KEYFRAME_INTERVAL = 'KEYFRAME_INTERVAL',
  MAX_AUDIO_BITRATE = 'EXCEEDS_MAX_AUDIO_BITRATE',
  MAX_AUDIO_CHANNELS = 'EXCEEDS_MAX_AUDIO_CHANNELS',
  MAX_AUDIO_CHANNELS_WIDE = 'EXCEEDS_MAX_AUDIO_CHANNELS_WIDE',
  MAX_DURATION = 'MAX_DURATION',
  MAX_DURATION_RELAXED = 'MAX_DURATION_RELAXED',
  MAX_FILE_SIZE = 'MAX_FILE_SIZE',
  MAX_FILE_SIZE_RELAXED = 'MAX_FILE_SIZE_RELAXED',
  MAX_GOP = 'MAX-GOP',
  MAX_RESOLUTION = 'MAX_RESOLUTION',
  MAX_RESOLUTION_4K = 'MAX_RESOLUTION_4K',
  MAX_VIDEO_BITRATE = 'EXCEEDS_MAX_VIDEO_BITRATE',
  MAX_VIDEO_BITRATE_4K = 'EXCEEDS_MAX_VIDEO_BITRATE_4K',
  MAX_VIDEO_BITRATE_WIDE = 'EXCEEDS_MAX_VIDEO_BITRATE_WIDE',
  MAX_VIDEO_CHANNELS = 'EXCEEDS_MAX_VIDEO_CHANNELS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  NOT_A_VIDEO = 'NOT_A_VIDEO',
  NO_AUDIO = 'NO_AUDIO',
  NO_UPLOAD_FILE = 'NO_UPLOAD_FILE',
}

export enum CHECKSUM_MESSAGE {
  ERROR = 'error',
  PROGRESS = 'progress',
  SUCCESS = 'success',
}

export type ErrorMessage = React.ReactNode | string;

export enum DescriptorTag {
  DecoderConfigDescriptorTag = 0x04,
  DecoderSpecificInfoTag = 0x05,
}
